"use client";

import { SVGIconProps } from "@faire/slate/icons/types";
import { componentCls } from "@faire/slate/lib/tracking";
import { tsv } from "@faire/slate/lib/tsv";
import { ButtonBase } from "@faire/slate/private/button/ButtonBase";
import { ButtonIcon } from "@faire/slate/private/button/ButtonIcon";
import type {
  IconButtonSize,
  IconButtonVariant,
  IButtonWebProps,
  IconButtonShape,
} from "@faire/slate/private/button/types";
import { usePolimorphicAttributes } from "@faire/slate/private/button/usePolimorphicAttributes";
import { useShouldShowSpinner } from "@faire/slate/private/button/useShouldShowSpinner";
import React from "react";

type IProps = {
  /**
   * Button variant
   *
   * @default "primary"
   */
  variant?: IconButtonVariant;
  /**
   * Button shape
   *
   * @default "circle"
   */
  shape?: IconButtonShape;
  /**
   * Button size
   *
   * xSmall (size 24px), small (size 40px), medium (size 48px)
   *
   * @default "medium"
   */
  size?: IconButtonSize;
  /**
   * Shows a loading spinner.
   *
   * @default false
   */
  loading?: boolean;
  /**
   * Whether the button is disabled.
   *
   * @default false
   */
  disabled?: boolean;
  /**
   * The icon component to render
   */
  Icon: React.FC<SVGIconProps>;

  // DO NOT ADD any new prop without aligning with Slate team first
} & IButtonWebProps;

/**
 * Help users take action or make a choice in smaller, refined spaces.
 *
 * See [migration guide](migration-guide.mdx) for more information.
 *
 * @trackcomponent
 * @version 2.1.0
 */
export const IconButton: React.FC<IProps> = ({
  "aria-label": ariaLabel,
  disabled,
  Icon,
  loading,
  onClick,
  shape = "circle",
  size = "medium",
  variant = "primary",
  ...props
}) => {
  const { handleClick, shouldShowSpinner, isLoading } = useShouldShowSpinner(
    disabled,
    loading,
    onClick
  );
  const { as, linkProps } = usePolimorphicAttributes(props);

  const iconButtonCls = iconButtonStyle.cls({
    variant,
    shape,
    size,
    className: componentCls(),
  });

  const iconButtonTouchAreaCls = iconButtonTouchAreaStyle.cls();

  const { linkTo, ...rest } = props;

  return (
    <ButtonBase
      {...rest}
      {...linkProps}
      aria-disabled={isLoading || disabled}
      aria-label={shouldShowSpinner ? undefined : ariaLabel}
      as={as}
      className={iconButtonCls}
      disabled={disabled}
      loading={shouldShowSpinner}
      onClick={handleClick}
    >
      <span className={iconButtonTouchAreaCls} />
      <ButtonIcon Icon={Icon} titleAccess="" />
    </ButtonBase>
  );
};

const iconButtonStyle = tsv({
  base: "shrink-0",
  variants: {
    variant: {
      primary: "fs-button--primary",
      secondary: "fs-button--secondary",
      tertiary: "fs-button--tertiary",
    },
    shape: {
      circle: "rounded-(--slate-radius-component-round)",
      square: null,
    },
    size: {
      xSmall: "fs-icon-button--x-small",
      small: "fs-icon-button--small",
      experimental32DoNotUse: "fs-icon-button--experimental-32",
      medium: "fs-icon-button--medium",
    },
  },
});

const iconButtonTouchAreaStyle = tsv({
  base: "absolute size-(--fs-btn-touch-area-size) rounded-[inherit]",
});
